/**
 * stockOutages.js — single source of truth for temporary stock outages,
 * per market.
 *
 * IDENTICAL COPIES live in the shop repo (src/data/) and the calculator
 * repo (src/data/). Keep them in sync — or set STOCK_OUTAGES_URL so both
 * apps read one hosted JSON file and this static list becomes a fallback.
 *
 * ── How to use ───────────────────────────────────────────────────────
 *   Stock runs out:   add an entry to STOCK_OUTAGES_DEFAULT (or the JSON)
 *   Stock returns:    delete the entry
 *
 *   Entry shape (keyed by productIDNum as a string):
 *     '404': {
 *         markets: ['CA'],          // required: 'US', 'CA', or both
 *         hide: false,              // optional: true = remove from grid
 *                                   //   entirely instead of showing an
 *                                   //   "out of stock" state
 *         note: 'Restock late Oct', // optional: shown to the customer
 *     }
 *
 * ── Relationship to onHandQty in data/index.js ───────────────────────
 *   onHandQty is the permanent "never list this" switch (placeholder
 *   items, discontinued SKUs). It is market-blind. Outages are temporary
 *   and per-market, so they live here instead. Do NOT use onHandQty for
 *   a stockout any more — leave it at its normal value.
 *
 * ── Zero-deploy mode (optional) ──────────────────────────────────────
 *   Host a JSON file (see public/stock-outages.json for the format) at a
 *   stable URL on electrobraid.com and set STOCK_OUTAGES_URL to it. Both
 *   apps fetch it on load with cache disabled, so editing that one file
 *   changes stock state everywhere without rebuilding either app. If the
 *   fetch fails for any reason, the static default below is used.
 */

import { useEffect, useState } from 'react'

// Set to e.g. 'https://electrobraid.com/shop/stock-outages.json' to enable
// remote control of outages. null = static list only.
export const STOCK_OUTAGES_URL = null

export const MARKETS = ['US', 'CA']

export const STOCK_OUTAGES_DEFAULT = {
    // AS1-EB — 1 inch heavy-duty insulator screws
    // 404: {
    //     markets: ['CA'],
    //     note: 'Canadian warehouse restock pending. Expected replenishment Oct 12.',
    // },
    // // IWHDCLAWB-EB — heavy-duty line post insulators (black)
    // 403: {
    //     markets: ['CA'],
    //     note: 'Canadian warehouse restock pending. Expected replenishment Oct 12.',
    // },
}

// ─── Pure helpers (no React) ──────────────────────────────────────────

/** 'CA' when the store is in CAD mode, otherwise 'US'. */
export function marketFromIsCAD(isCAD) {
    return isCAD ? 'CA' : 'US'
}

/** Human-readable market name for customer-facing copy. */
export function marketLabel(market) {
    return market === 'CA' ? 'Canada' : 'the US'
}

/** True if productIDNum is out of stock in the given market. */
export function isOutOfStock(outages, productIDNum, market) {
    const entry = outages && outages[String(productIDNum)]
    return (
        !!entry &&
        Array.isArray(entry.markets) &&
        entry.markets.includes(market)
    )
}

/** True if the product should be removed from the grid (not just marked). */
export function isHiddenWhenOut(outages, productIDNum, market) {
    return (
        isOutOfStock(outages, productIDNum, market) &&
        outages[String(productIDNum)].hide === true
    )
}

/** Optional customer-facing note for an out-of-stock product, or ''. */
export function outageNote(outages, productIDNum) {
    const entry = outages && outages[String(productIDNum)]
    return (entry && entry.note) || ''
}

/** All productIDNums out of stock in the given market. */
export function outOfStockIds(outages, market) {
    return Object.keys(outages || {}).filter((id) =>
        isOutOfStock(outages, id, market)
    )
}

/**
 * Defensive normalisation of a remotely loaded outage map: drops
 * malformed entries and unknown markets so a typo in the JSON can never
 * crash the app or accidentally block a product everywhere.
 */
export function normalizeOutages(raw) {
    if (!raw || typeof raw !== 'object') return {}
    const clean = {}
    for (const [id, entry] of Object.entries(raw)) {
        if (!entry || typeof entry !== 'object') continue
        const markets = Array.isArray(entry.markets)
            ? entry.markets
                  .map((m) => String(m).toUpperCase())
                  .filter((m) => MARKETS.includes(m))
            : []
        if (markets.length === 0) continue
        clean[String(id)] = {
            markets,
            hide: entry.hide === true,
            note: typeof entry.note === 'string' ? entry.note : '',
        }
    }
    return clean
}

// ─── React hook ───────────────────────────────────────────────────────

/**
 * Returns the current outage map. Starts with the static default so the
 * first render is already correct; if STOCK_OUTAGES_URL is set, replaces
 * it with the remote list once loaded. Remote failures are silent — the
 * static list stays in effect.
 */
export function useStockOutages() {
    const [outages, setOutages] = useState(() =>
        normalizeOutages(STOCK_OUTAGES_DEFAULT)
    )

    useEffect(() => {
        if (!STOCK_OUTAGES_URL) return undefined
        let cancelled = false

        fetch(STOCK_OUTAGES_URL, { cache: 'no-store' })
            .then((res) =>
                res.ok ? res.json() : Promise.reject(new Error(res.status))
            )
            .then((json) => {
                if (cancelled) return
                // Accept either { outages: {...} } or a bare map.
                const map =
                    json && typeof json.outages === 'object'
                        ? json.outages
                        : json
                setOutages(normalizeOutages(map))
            })
            .catch(() => {
                /* keep static default */
            })

        return () => {
            cancelled = true
        }
    }, [])

    return outages
}
